"use client"

import React from 'react'
import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, Calendar, Clock, CreditCard, RefreshCw, Phone, RefreshCcw, CalendarClock, Info, MapPin } from 'lucide-react'
import { CardContent, CardFooter, CardHeader, CardTitle, CardV2 } from '../CardV2/CardV2'
import { ButtonV2 } from '../ButtonV2/ButtonV2'
import { useTranslation } from 'react-i18next'
import { BookingDetailsDto } from '../../api/BookingAPI'
import { Badge } from '../Badge/Badge'
import { bookingPaymentStatusToTranslationKey } from '../../shared/paymentStatusToTranslationKey'
import { bookingStatusToTranslationKey } from '../../shared/bookingStatusToTranslationKey'

export interface BookingFeedbackProps {
  state: 'success' | 'failure' | 'pending' | 'cancelled'
  bookingDetails?: BookingDetailsDto
  storeName: string
  storeAddress: string
  contactPhone: string
  onRetry: () => void
  onRetryPayment?: () => void
  failureReason?: string
  onDismiss?: () => void
}

export const BookingFeedback: React.FC<BookingFeedbackProps> = ({
  state,
  bookingDetails,
  storeName,
  storeAddress,
  contactPhone,
  onRetry,
  onRetryPayment,
  failureReason,
  onDismiss
}) => {
  const [showConfetti, setShowConfetti] = useState(state === 'success')
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => setShowConfetti(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [state])

  // Helper to format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) {
        return dateStr;
      }
      return date.toLocaleDateString(i18n.language || undefined, { dateStyle: 'long' });
    } catch {
      return dateStr;
    }
  };

  // Helper to format time
  const formatTime = (timeStr: string) => {
    try {
      const [hours, minutes] = timeStr.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return timeStr;
    }
  };

  // Determine overall status based on booking and payment status
  const getOverallStatus = () => {
    // Special case for TOO_MANY_BOOKINGS - treat it as a special informational state
    if (failureReason === 'TOO_MANY_BOOKINGS') {
      return 'info'; // New status type for informational messages
    }

    if (!bookingDetails) return state;

    const bookingStatus = bookingDetails?.pendingBooking?.status;
    const paymentStatus = bookingDetails?.paymentStatus?.status;

    if (bookingStatus === 'APPROVED') return 'success';

    // Treat cancellations separately from failures
    if (bookingStatus === 'REJECTED' || bookingStatus === 'CANCELED_BY_BRIDE') return 'cancelled';

    // If booking is pending explicitly show as pending
    if (bookingStatus === 'PENDING') {
      return 'pending';
    }

    // If payment explicitly failed
    if (paymentStatus === 'FAILED' || paymentStatus === 'DISPUTED' || paymentStatus === 'CANCELED') return 'failure';

    // Default to the provided state
    return state;
  };

  // Get status icon based on overall status
  const getStatusIcon = () => {
    const overallStatus = getOverallStatus();

    if (overallStatus === 'success') {
      return <CheckCircle className="h-12 w-12 text-green-500" />;
    }

    if (overallStatus === 'cancelled') {
      return <XCircle className="h-12 w-12 text-destructive" />;
    }

    if (overallStatus === 'failure' || overallStatus === 'info') {
      if (failureReason === 'TOO_MANY_BOOKINGS') {
        return <Info className="h-12 w-12 text-blue-500" />;
      }
      return <XCircle className="h-12 w-12 text-destructive" />;
    }

    // For pending state
    return <Clock className="w-16 h-16 text-amber-500" />;
  };

  // Get status title
  const getStatusTitle = () => {
    const overallStatus = getOverallStatus();

    if (failureReason === 'TOO_MANY_BOOKINGS') {
      return t('booking.feedback.failure.too_many_bookings', 'Booking Notice');
    }
    if (overallStatus === 'success') return t('booking.feedback.success.confirmed');
    if (overallStatus === 'cancelled') return t('booking.feedback.cancelled.title');
    if (overallStatus === 'failure') {
      if (failureReason) {
        if (failureReason === 'booking.payment.failed') {
          return t('booking.feedback.payment_failure.title');
        }
        if (failureReason === 'booking.payment.timeout') {
          return t('booking.feedback.payment_timeout.title');
        }
        if (failureReason === 'booking.canceled') {
          return t('booking.feedback.cancelled.title');
        }
        if (failureReason === 'PAYMENT_PROCESSING_ERROR') {
          return t('booking.feedback.payment_processing_error.title', 'Payment Processing Error');
        }
        // Fall back to the provided reason if it's a valid translation key
        return t(failureReason) || t('booking.feedback.failure.title');
      }
      return t('booking.feedback.failure.title');
    }

    return t('booking.feedback.pending.title');
  };

  // Get status message
  const getStatusMessage = () => {
    // Special case for TOO_MANY_BOOKINGS
    if (failureReason === 'TOO_MANY_BOOKINGS') {
      return t('booking.feedback.failure.booking_limit_explanation', 'It seems you already have a booking confirmed or pending confirmation. The salon has limited the number of bookings that can be made in advance.');
    }

    const overallStatus = getOverallStatus();

    if (overallStatus === 'success') {
      return bookingDetails?.paymentStatus?.status === 'SUCCEEDED'
        ? t('booking.feedback.payment_success.message')
        : t('booking.feedback.success.message');
    }

    if (overallStatus === 'cancelled') {
      return t('booking.feedback.cancelled.message');
    }

    if (overallStatus === 'failure') {
      if (failureReason) {
        if (failureReason === 'booking.payment.failed') {
          return t('booking.feedback.payment_failure.message');
        }
        if (failureReason === 'booking.payment.timeout') {
          return t('booking.feedback.payment_timeout.message');
        }
        if (failureReason === 'booking.canceled') {
          return t('booking.feedback.cancelled.message');
        }
        if (failureReason === 'PAYMENT_PROCESSING_ERROR') {
          return t('booking.feedback.payment_processing_error.message', 'We encountered an error while processing your payment. Please try again or contact the store for assistance.');
        }
        // Fall back to the provided reason if it's a valid translation key
        return t(failureReason) || t('booking.feedback.failure.message');
      }
      return t('booking.feedback.failure.message');
    }

    return t('booking.feedback.pending.message');
  };

  // Render payment information section if available
  const renderPaymentInfo = () => {
    if (!bookingDetails?.paymentStatus) return null;

    const payment = bookingDetails.paymentStatus;
    const useOnlinePayments = bookingDetails?.bookingPlan?.useOnlinePayments !== false;

    // Don't show payment section if payment was not required
    if (payment.status === 'NOT_REQUIRED') return null;

    return (
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5" />
          {t('booking.feedback.payment_info')}
        </h3>

        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
            <span className="text-gray-500 text-sm">{t('booking.feedback.payment_status')}</span>
            <PaymentStatusBadge status={payment.status} />
          </div>

          {payment.amount && payment.currency && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
              <span className="text-gray-500 text-sm">{t('booking.feedback.payment_amount')}</span>
              <span className="text-sm sm:text-base">{payment.currency} {payment.amount}</span>
            </div>
          )}

          {payment.isRefundable && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
              <span className="text-gray-500 text-sm">{t('booking.feedback.refundable')}</span>
              <span className="text-sm sm:text-base">{t('global.yes')}</span>
            </div>
          )}

          {payment.isRefundable && payment.refundedAmount && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
              <span className="text-gray-500 text-sm">{t('booking.feedback.refunded_amount')}</span>
              <span className="text-sm sm:text-base">{payment.currency} {payment.refundedAmount}</span>
            </div>
          )}
        </div>

        {/* Add retry payment button if payment failed */}
        {!!onRetryPayment && (
          <div className="mt-4 flex justify-end">
            <ButtonV2
              onClick={onRetryPayment}
              variant="default"
              size="sm"
              className="flex items-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              {t('booking.feedback.failure.retry_payment')}
            </ButtonV2>
          </div>
        )}

        {/* Show offline payment notice if configured */}
        {!useOnlinePayments && (payment.status === 'PENDING' || payment.status === 'FAILED') && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start space-x-2">
              <MapPin className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-amber-700 text-sm">
                <p className="font-medium">{t('booking.payment.offline_notice.title') || 'Payment Required'}</p>
                <p>{t('booking.payment.offline_notice.message') || 'The store will collect payment directly. Please bring payment or make arrangements with the store before your appointment if needed.'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Payment status badge
  const PaymentStatusBadge = ({ status }: { status: string }) => {
    const { t } = useTranslation();

    switch (status) {
      case 'SUCCEEDED':
        return <Badge variant="default">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      case 'FAILED':
        return <Badge variant="destructive">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      case 'PENDING':
        return <Badge variant="outline">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      case 'REFUNDED':
        return <Badge variant="secondary">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      case 'DISPUTED':
        return <Badge variant="destructive">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      case 'CANCELED':
        return <Badge variant="secondary">{t(bookingPaymentStatusToTranslationKey(status))}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Add a helper function to check if meeting times have been updated
  const hasMeetingChanges = () => {
    return bookingDetails?.meetingChanges?.updatedStartDate ||
           bookingDetails?.meetingChanges?.updatedStartTime;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full px-2 sm:px-0"
    >
      <CardV2 className="w-full border-0 shadow-none">
        <CardHeader className="text-center px-4 sm:px-6">
          <div className="flex flex-col items-center space-y-4">
            {getStatusIcon()}
            <CardTitle className="text-xl sm:text-2xl">{getStatusTitle()}</CardTitle>
            <p className="text-muted-foreground text-sm sm:text-base px-2">{getStatusMessage()}</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 px-4 sm:px-6">
          {getOverallStatus() === 'success' || getOverallStatus() === 'pending' || getOverallStatus() === 'cancelled' ? (
            <>
              {bookingDetails ? (
                <div className={`${getOverallStatus() === 'cancelled' ? 'bg-destructive/10' : 'bg-primary/10'} rounded-lg p-4 sm:p-6 space-y-4`}>
                  <h3 className="font-semibold text-lg">
                    {getOverallStatus() === 'cancelled'
                      ? t("booking.feedback.common.summary")
                      : t("booking.feedback.common.summary")}:
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Meeting Type */}
                    {bookingDetails?.bookingPlan?.name && (
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm sm:text-base">{bookingDetails.bookingPlan.name}</span>
                      </div>
                    )}

                    {/* Date and Time */}
                    {(bookingDetails?.pendingBooking?.appointmentDate || bookingDetails?.pendingBooking?.appointmentTime) && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm sm:text-base">
                          {bookingDetails.pendingBooking.appointmentDate &&
                           formatDate(bookingDetails.pendingBooking.appointmentDate)}
                          {bookingDetails.pendingBooking.appointmentDate && bookingDetails.pendingBooking.appointmentTime &&
                           ` ${t("booking.confirmation.hour_at")} `}
                          {bookingDetails.pendingBooking.appointmentTime &&
                           formatTime(bookingDetails.pendingBooking.appointmentTime)}
                        </span>
                      </div>
                    )}

                    {/* Show updated appointment time if available */}
                    {hasMeetingChanges() && (
                      <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-md lg:col-span-2">
                        <RefreshCcw className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm sm:text-base">
                          <p className="font-medium text-blue-700">{t("booking.feedback.updated_appointment")}:</p>
                          <p>
                            {formatDate(bookingDetails?.meetingChanges?.updatedStartDate || '')}
                            {` ${t("booking.confirmation.hour_at")} `}
                            {formatTime(bookingDetails?.meetingChanges?.updatedStartTime || '')}

                            {bookingDetails!.meetingChanges!.updatedEndTime && (
                              <> - {formatTime(bookingDetails!.meetingChanges!.updatedEndTime)}</>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Duration */}
                    {bookingDetails?.bookingPlan?.duration && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm sm:text-base">
                          {t("booking.feedback.common.duration")}: {bookingDetails.bookingPlan.duration} {t("bookings.form.step1.appointment.minutes")}
                        </span>
                      </div>
                    )}

                    {/* Fee */}
                    {bookingDetails?.bookingPlan?.fee !== undefined &&
                     bookingDetails.bookingPlan.fee &&
                     bookingDetails.bookingPlan.fee > 0 && (
                      <div className="flex items-start gap-3">
                        <CreditCard className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm sm:text-base">
                          {t("booking.feedback.common.fee")}:{" "}
                          {`${bookingDetails.bookingPlan.feeCurrency || ''} ${bookingDetails.bookingPlan.fee}`}
                        </span>
                      </div>
                    )}
                    {/* TODO: For free bookings (no fee), hide the explicit
                        "no fee"/free-style label in this summary block for now.
                        Previous behaviour used:
                        t("booking.feedback.common.fee.none") when fee was falsy. */}

                    {/* Refundable Fee */}
                    {bookingDetails?.bookingPlan?.requiresRefundableFee && bookingDetails.bookingPlan.refundableFee && (
                      <>
                        <div className="flex items-start gap-3">
                          <RefreshCcw className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm sm:text-base">
                            {t("booking.feedback.success.refundable_fee")}:{" "}
                            {bookingDetails.bookingPlan.refundableFeeCurrency} {bookingDetails.bookingPlan.refundableFee}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground italic lg:col-span-2">
                          {t("bookings.form.step1.appointment.refundable_fee.note")}
                        </div>
                      </>
                    )}

                    {/* Offline Payment Notice */}
                    {bookingDetails?.bookingPlan?.useOnlinePayments === false && 
                     (bookingDetails?.bookingPlan?.requiresFee || bookingDetails?.bookingPlan?.requiresRefundableFee) && (
                      <div className="flex items-start gap-3 lg:col-span-2">
                        <MapPin className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm sm:text-base">
                          <span className="font-medium text-amber-700">
                            {t("booking.payment.offline_notice.title") || "Payment Collection"}
                          </span>
                          <p className="text-amber-600 mt-1">
                            {t("booking.payment.offline_notice.message") || "The store will collect payment directly. Please bring payment or make arrangements with the store before your appointment if needed."}
                          </p>
                          
                          {/* Show deposit and total information if available */}
                          {bookingDetails?.bookingPlan && (
                            <div className="mt-3 space-y-1">
                              {bookingDetails.bookingPlan.requiresFee && bookingDetails.bookingPlan.feeIsDeposit && (
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-amber-700">{t('booking.payment.offline_notice.deposit') || 'Deposit to confirm reservation:'}</span>
                                  <span className="font-semibold text-amber-800">{bookingDetails.bookingPlan.feeCurrency} {bookingDetails.bookingPlan.fee}</span>
                                </div>
                              )}
                              {bookingDetails.bookingPlan.totalPrice && (
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-amber-700">{t('booking.payment.offline_notice.total') || 'Total amount:'}</span>
                                  <span className="font-semibold text-amber-800">{bookingDetails.bookingPlan.totalPriceCurrency || bookingDetails.bookingPlan.feeCurrency} {bookingDetails.bookingPlan.totalPrice}</span>
                                </div>
                              )}
                              {bookingDetails.bookingPlan.requiresFee && bookingDetails.bookingPlan.feeIsDeposit && bookingDetails.bookingPlan.totalPrice && (
                                <div className="flex justify-between items-center text-xs text-amber-600">
                                  <span>{t('booking.payment.offline_notice.remaining') || 'Remaining to pay later:'}</span>
                                  <span>{bookingDetails.bookingPlan.totalPriceCurrency || bookingDetails.bookingPlan.feeCurrency} {Math.max(0, bookingDetails.bookingPlan.totalPrice - bookingDetails.bookingPlan.fee)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Booking Status */}
                    {bookingDetails?.pendingBooking?.status && (
                      <div className="flex items-start gap-3 lg:col-span-2">
                        <CalendarClock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm sm:text-base">
                          {t("booking.feedback.booking_status")}: {' '}
                          <BookingStatusBadge status={bookingDetails.pendingBooking.status} />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-600">
                    {t("booking.feedback.no_details")}
                  </p>
                </div>
              )}

              {/* Render detailed payment information if available */}
              {renderPaymentInfo()}

              {/* Add a cancelled notice if the booking is cancelled */}
              {getOverallStatus() === 'cancelled' && (
                <div className="bg-destructive/10 rounded-lg p-4 text-sm">
                  <h4 className="font-semibold mb-2">{t("booking.feedback.cancelled.notice")}:</h4>
                  <p className="text-sm sm:text-base">{t("booking.feedback.cancelled.explanation")}</p>
                </div>
              )}

              {/* Only show success email notice for successful bookings */}
              {getOverallStatus() === 'success' && (
                <div className="bg-secondary/20 rounded-lg p-4 text-sm">
                  <p className="text-sm sm:text-base">
                    {t("booking.feedback.success.email.p1")}{' '}
                    {t("booking.feedback.success.email.p2")}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {failureReason === "TOO_MANY_BOOKINGS" ? (
                <div className="bg-blue-50 rounded-lg p-4 sm:p-6 space-y-4">
                  <h3 className="font-semibold text-lg">{t("booking.feedback.failure.booking_limit_reached")}:</h3>
                  <p className="text-sm sm:text-base">{t("booking.feedback.failure.booking_limit_explanation")}.</p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base">
                    <li>{t("booking.feedback.failure.booking_limit.p1")}.</li>
                    <li>{t("booking.feedback.failure.booking_limit.p2")}.</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-destructive/10 rounded-lg p-4 sm:p-6 space-y-4">
                  <h3 className="font-semibold text-lg">{t("booking.feedback.failure.next_steps")}:</h3>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base">
                    <li>{t("booking.feedback.failure.next_steps.p1")}</li>
                    <li>{t("booking.feedback.failure.next_steps.p2")}</li>
                    <li>{t("booking.feedback.failure.next_steps.p3")}</li>
                  </ul>
                </div>
              )}

              {/* Render payment info in failure case also */}
              {renderPaymentInfo()}

              <div className="space-y-3">
                <h3 className="font-semibold text-lg">{t("booking.feedback.failure.contact")}:</h3>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div className="text-sm sm:text-base">
                    <p className="font-medium">{storeName}</p>
                    <p>{contactPhone}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4 sm:px-6 pb-4 sm:pb-6">
          {getOverallStatus() !== 'success' 
          && getOverallStatus() !== 'pending' 
          && getOverallStatus() !== 'cancelled' && (
            <>
              <ButtonV2 variant="outline" onClick={onRetry} className="w-full sm:w-auto">
                <RefreshCw className="mr-2 h-4 w-4" />
                { t("booking.feedback.failure.retry") }
              </ButtonV2>
            </>
          )}
        </CardFooter>
      </CardV2>
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none"
          >
            {Array.from({ length: 50 }).map((_, index) => (
              <motion.div
                key={index}
                className="absolute w-2 h-2 bg-primary rounded-full"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -10,
                  opacity: 1
                }}
                animate={{
                  y: window.innerHeight + 10,
                  opacity: 0
                }}
                transition={{
                  duration: Math.random() * 2 + 1,
                  repeat: Infinity,
                  repeatDelay: Math.random() * 3
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const BookingStatusBadge = ({ status }: { status?: string }) => {
  const { t } = useTranslation();

  if (!status) return <Badge>Unknown</Badge>;

  switch (status) {
    case 'APPROVED':
      return <Badge variant="default">{t(bookingStatusToTranslationKey(status))}</Badge>;
    case 'REJECTED':
      return <Badge variant="destructive">{t(bookingStatusToTranslationKey(status))}</Badge>;
    case 'CANCELED_BY_BRIDE':
      return <Badge variant="secondary">{t(bookingStatusToTranslationKey(status))}</Badge>;
    case 'PENDING':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200">{t(bookingStatusToTranslationKey(status))}</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};